import { ref } from "vue"
import { defineStore } from "pinia"
import { UseRootStore } from "@/model/RootStore"
import { RegulatoryAssistantFailure, RegulatoryMessageKind, type RegulatoryMessage } from "@/types/regulatoryAssistant"
import * as apiClient from "@/utils/ApiClient"

const GENERAL_CONVERSATION_KEY = "general"

export const useRegulatoryAssistantStore = defineStore("regulatoryAssistant", () => {
	const rootStore = UseRootStore()
	const conversationsByKey = ref<Record<string, RegulatoryMessage[]>>({})
	const requestStartedAtByKey = ref<Record<string, number>>({})
	const requestControllersByKey = new Map<string, AbortController>()
	let messageSequence = 0

	function conversationKey(parcelId: string | null): string {
		return parcelId ?? GENERAL_CONVERSATION_KEY
	}

	function nextMessageId(): string {
		messageSequence += 1
		return String(messageSequence)
	}

	function messages(parcelId: string | null): RegulatoryMessage[] {
		return conversationsByKey.value[conversationKey(parcelId)] ?? []
	}

	function isBusy(parcelId: string | null): boolean {
		return conversationKey(parcelId) in requestStartedAtByKey.value
	}

	function requestStartedAt(parcelId: string | null): number | null {
		return requestStartedAtByKey.value[conversationKey(parcelId)] ?? null
	}

	function append(parcelId: string | null, message: RegulatoryMessage): void {
		conversationsByKey.value[conversationKey(parcelId)] = [...messages(parcelId), message]
	}

	async function request(parcelId: string | null, question: string): Promise<void> {
		const key = conversationKey(parcelId)
		const controller = new AbortController()
		const startedAt = Date.now()
		requestControllersByKey.set(key, controller)
		requestStartedAtByKey.value[key] = startedAt

		try {
			const response = await apiClient.askRegulatoryAssistant(
				rootStore.userApiToken,
				question,
				parcelId,
				controller.signal
			)
			append(parcelId, {
				kind: RegulatoryMessageKind.Answer,
				id: nextMessageId(),
				response,
				durationMs: Date.now() - startedAt,
			})
		} catch (error) {
			if (!controller.signal.aborted) {
				console.error("Regulatory assistant request failed:", error)
			}
			append(parcelId, {
				kind: RegulatoryMessageKind.Error,
				id: nextMessageId(),
				failure: controller.signal.aborted ? RegulatoryAssistantFailure.Stopped : RegulatoryAssistantFailure.Failed,
				question,
				durationMs: Date.now() - startedAt,
			})
		} finally {
			requestControllersByKey.delete(key)
			delete requestStartedAtByKey.value[key]
		}
	}

	async function ask(parcelId: string | null, question: string): Promise<void> {
		const trimmedQuestion = question.trim()
		if (!trimmedQuestion || isBusy(parcelId)) return

		append(parcelId, { kind: RegulatoryMessageKind.Question, id: nextMessageId(), question: trimmedQuestion })
		await request(parcelId, trimmedQuestion)
	}

	async function retry(parcelId: string | null, errorMessageId: string): Promise<void> {
		const errorMessage = messages(parcelId).at(-1)
		if (errorMessage?.id !== errorMessageId || errorMessage.kind !== RegulatoryMessageKind.Error || isBusy(parcelId))
			return

		conversationsByKey.value[conversationKey(parcelId)] = messages(parcelId).filter(
			message => message.id !== errorMessageId
		)
		await request(parcelId, errorMessage.question)
	}

	function stop(parcelId: string | null): void {
		requestControllersByKey.get(conversationKey(parcelId))?.abort()
	}

	function clear(parcelId: string | null): void {
		if (isBusy(parcelId)) return
		delete conversationsByKey.value[conversationKey(parcelId)]
	}

	return { messages, isBusy, requestStartedAt, ask, retry, stop, clear }
})
